import React, { useState, useRef, useEffect } from 'react';
import { DotsVerticalIcon } from '@heroicons/react/solid';
import ReactDOM from 'react-dom';

interface IOption {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
}

interface IOptionMenuProps {
  options: (IOption | null)[];
  containerClassNames?: string;
  triggerLabel?: string;
}

const OptionMenu = ({
  options = [],
  containerClassNames,
  triggerLabel = 'More actions',
}: IOptionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const menuPosition = buttonRef.current
    ? {
        top: buttonRef.current.getBoundingClientRect().bottom + window.scrollY,
        left: buttonRef.current.getBoundingClientRect().left + window.scrollX,
      }
    : { top: 0, left: 0 };

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        aria-label={triggerLabel}
        aria-expanded={isOpen}
        className="p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
        onClick={toggleMenu}
      >
        <DotsVerticalIcon className="h-6 w-6" aria-hidden="true" />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className={`absolute bg-white border border-gray-300 rounded-md shadow-lg z-[100000] ${
              containerClassNames ?? ''
            }`}
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left - 20}px`,
            }}
          >
            {options.map(
              (option, index) =>
                option && (
                  <button
                    type="button"
                    key={index}
                    className="block w-full flex items-center text-left px-4 py-2 hover:bg-gray-100 focus:outline-none"
                    onClick={() => {
                      option.onClick();
                      setIsOpen(false);
                    }}
                  >
                    {option.icon ? option.icon : null}
                    {option.label}
                  </button>
                ),
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OptionMenu;
